import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBackCloses } from './router';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Photo } from './Photo';

/** Inline stroke icons, sized by the `size` prop and coloured by `currentColor`. */
type IconProps = { size?: number; strokeWidth?: number };

function svg(path: ReactNode, { size = 22, strokeWidth = 2 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

export const IconCamera = (p: IconProps) =>
  svg(
    <>
      <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.2l1.3-2h8l1.3 2h1.2A2.5 2.5 0 0 1 21 8.5v8A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5v-8Z" />
      <circle cx="12" cy="12.4" r="3.4" />
    </>,
    p,
  );

export const IconGrid = (p: IconProps) =>
  svg(
    <>
      <rect x="4" y="4.5" width="7" height="7" rx="2" />
      <rect x="13" y="4.5" width="7" height="7" rx="2" />
      <rect x="4" y="13" width="7" height="7" rx="2" />
      <rect x="13" y="13" width="7" height="7" rx="2" />
    </>,
    p,
  );

/** The ring, which is what an unopened GiviSnap looks like everywhere else. */
/** A clock wound backwards. */
export const IconMemory = (p: IconProps) =>
  svg(
    <>
      <path d="M3.6 9.2a8.5 8.5 0 1 1-.4 4.8" />
      <path d="M3 4.5v5h5" />
      <path d="M12 8.2v4.3l2.9 1.7" />
    </>,
    p,
  );

export const IconChat = (p: IconProps) =>
  svg(<path d="M4.5 6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4.5 4v-13Z" />, p);

/*
 * Filled when it is kept. A tinted background alone was the whole signal and
 * it was too quiet to notice on a 44px button over a photograph — the shape
 * itself has to change.
 */
export const IconBookmark = ({ filled, ...p }: IconProps & { filled?: boolean }) => (
  <svg
    width={p.size ?? 22}
    height={p.size ?? 22}
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={p.strokeWidth ?? 1.8}
    aria-hidden="true"
  >
    <path d="M6.5 4.5h11a1 1 0 0 1 1 1v14l-6.5-4-6.5 4v-14a1 1 0 0 1 1-1Z" />
  </svg>
);

export const IconHeart = ({ filled, ...p }: IconProps & { filled?: boolean }) => (
  <svg
    width={p.size ?? 22}
    height={p.size ?? 22}
    viewBox="0 0 24 24"
    fill={filled ? 'currentColor' : 'none'}
    stroke="currentColor"
    strokeWidth={p.strokeWidth ?? 1.8}
    aria-hidden="true"
  >
    <path d="M12 19.5s-6.8-4.3-6.8-9a3.8 3.8 0 0 1 6.8-2.3 3.8 3.8 0 0 1 6.8 2.3c0 4.7-6.8 9-6.8 9Z" />
  </svg>
);

export const IconPlus = (p: IconProps) => svg(<path d="M12 5v14M5 12h14" />, p);

/** Three bars of different lengths, which is what a result looks like. */
export const IconPoll = (p: IconProps) =>
  svg(<path d="M5 7.5h9M5 12h13M5 16.5h6" />, p);

export const IconPin = (p: IconProps) =>
  svg(
    <path d="M12 21s6.5-6 6.5-11a6.5 6.5 0 1 0-13 0c0 5 6.5 11 6.5 11Zm0-8.6a2.4 2.4 0 1 1 0-4.8 2.4 2.4 0 0 1 0 4.8Z" />,
    p,
  );

/** A sheet with its corner turned: whatever has no picture of its own. */
export const IconFile = (p: IconProps) =>
  svg(<path d="M13.5 3.5H7a1.5 1.5 0 0 0-1.5 1.5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.5Zm0 0V8.5h5" />, p);

/** iOS Share: a box with an arrow leaving the top. */
export const IconShare = (p: IconProps) =>
  svg(
    <>
      <path d="M12 3.5v11" />
      <path d="M8.5 7 12 3.5 15.5 7" />
      <path d="M7 10.5H5.5v9h13v-9H17" />
    </>,
    p,
  );

/** The "More" affordance: three dots in a row. */
export const IconDots = (p: IconProps) =>
  svg(
    <>
      <circle cx="5" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </>,
    p,
  );

/** iOS "View More" in the share sheet: a chevron down in a filled circle. */
export const IconViewMore = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 10.5l4 4 4-4" />
    </>,
    p,
  );

/** Add to Home Screen: a plus inside a rounded app tile. */
export const IconAddToHome = (p: IconProps) =>
  svg(
    <>
      <rect x="4" y="4" width="16" height="16" rx="4.5" />
      <path d="M12 9v6M9 12h6" />
    </>,
    p,
  );

/**
 * The app's own mark: a G with a lens iris at its centre, the bar running
 * into it so the two are one connected shape. Same geometry as icon.svg,
 * scaled from its 512 grid to this 24 one, which is why the stroke widths are
 * stated here rather than taken from the prop — the iris is about a third the
 * weight of the letter, and that ratio is the drawing.
 */
/** The iris, shared by the rim and the glass under it. */
const IRIS = 'M12 8.43 L15.09 10.22 L15.09 13.78 L12 15.57 L8.91 13.78 L8.91 10.22 Z';

export const IconAperture = ({ size = 22 }: IconProps) => {
  // One gradient per lens, so two on a page cannot take each other's. React's
  // ids carry punctuation that has no business inside url(#...).
  const pane = `aperture-glass-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`;
  return (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/*
      Same drawing as icon.svg but not the same proportions, deliberately. The
      app icon leaves a margin inside its tile; a UI icon has to fill its box or
      it reads a size smaller than everything beside it. So the ring is drawn at
      r=8 rather than the tile's 6.09, while the stroke stays near the 2 that
      the other icons use. Shifted 0.76 for the same reason the tile is shifted:
      the letter's opening means its ink is not centred on its circle.
    */}
    <g transform="translate(0.76 0)">
      {/*
        The lens, as the home-screen icon has it: glass paler at its upper
        left, with a glint, drawn first so the rim sits crisp over its edge.
        Every G has it, so the mark is the same mark wherever it is drawn.
        See-through rather than blue: light over whatever is behind it at
        the top, shade at the bottom. On the accent that comes out as the
        icon's own glass; on the dark bar, as dark glass.
      */}
      <defs>
        <linearGradient
          id={pane}
          x1="8.91"
          y1="8.43"
          x2="15.09"
          y2="15.57"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.28" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.2" />
        </linearGradient>
      </defs>
      <path d={IRIS} fill={`url(#${pane})`} stroke="none" />
      <ellipse
        cx="10.77"
        cy="10.52"
        rx="0.92"
        ry="0.49"
        transform="rotate(-30 10.77 10.52)"
        fill="#FFFFFF"
        fillOpacity="0.7"
        stroke="none"
      />
      <path d="M18.47 7.3 A 8 8 0 1 0 18.47 16.7 L18.47 12" strokeWidth="2.2" />
      {/* Its own path so the cap is flat and stops flush against the iris. */}
      <path d="M18.47 12 L15.09 12" strokeWidth="2.2" strokeLinecap="butt" />
      <path d={IRIS} strokeWidth="0.8" />
    </g>
  </svg>
  );
};

export const IconVideo = (p: IconProps) =>
  svg(
    <>
      <rect x="2.5" y="6.5" width="12" height="11" rx="3" />
      <path d="M14.5 11.2 L20.5 8 v8 l-6-3.2 z" />
    </>,
    p,
  );

export const IconMic = (p: IconProps) =>
  svg(
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" />
    </>,
    p,
  );

export const IconSearch = (p: IconProps) =>
  svg(
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20 20" />
    </>,
    p,
  );

/** The search glass with a plus or a minus in it: closer, or back to the whole. */
export const IconZoomIn = (p: IconProps) =>
  svg(
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20 20M8.5 11h5M11 8.5v5" />
    </>,
    p,
  );
export const IconZoomOut = (p: IconProps) =>
  svg(
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20 20M8.5 11h5" />
    </>,
    p,
  );
export const IconClose = (p: IconProps) => svg(<path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />, p);
export const IconCheck = (p: IconProps) => svg(<path d="M5 12.5 10 17.5 19.5 7.5" />, p);
export const IconBack = (p: IconProps) => svg(<path d="M14.5 5.5 8 12l6.5 6.5" />, p);
export const IconForward = (p: IconProps) => svg(<path d="M9.5 5.5 16 12l-6.5 6.5" />, p);
export const IconDown = (p: IconProps) => svg(<path d="M5.5 9 12 15.5 18.5 9" />, p);
export const IconSend = (p: IconProps) =>
  svg(
    <>
      <path d="M5 12h13" />
      <path d="M12.5 6.5 19 12l-6.5 5.5" />
    </>,
    p,
  );
export const IconDownload = (p: IconProps) =>
  svg(
    <>
      <path d="M12 4v11" />
      <path d="M8 11.5 12 15.5l4-4" />
      <path d="M5.5 19.5h13" />
    </>,
    p,
  );
export const IconGear = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 4.5v2M12 17.5v2M4.5 12h2M17.5 12h2M6.9 6.9l1.4 1.4M15.7 15.7l1.4 1.4M17.1 6.9l-1.4 1.4M8.3 15.7l-1.4 1.4" />
    </>,
    p,
  );
export const IconImage = (p: IconProps) =>
  svg(
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.6" />
      <circle cx="9" cy="10.5" r="1.7" />
      <path d="M4.5 16.6 9.6 12l3.4 3 2.6-2.1 4 4" />
    </>,
    p,
  );
export const IconSelfie = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="9" r="3.4" />
      <path d="M5.5 19.5a6.5 6.5 0 0 1 13 0" />
    </>,
    p,
  );
/** Front camera, back camera: two arrows chasing round. */
export const IconFlip = (p: IconProps) =>
  svg(
    <>
      <path d="M5 9.5a7 7 0 0 1 12.5-3.2L19 8" />
      <path d="M19 4v4h-4" />
      <path d="M19 14.5a7 7 0 0 1-12.5 3.2L5 16" />
      <path d="M5 20v-4h4" />
    </>,
    p,
  );
export const IconSmile = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.6 14.2a4.2 4.2 0 0 0 6.8 0" />
      <path d="M9.2 9.6h.01M14.8 9.6h.01" />
    </>,
    p,
  );
/** A face wearing glasses: the face stickers. */
export const IconFace = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M5.8 10.2h12.4" />
      <rect x="6.6" y="9.4" width="4.4" height="3.2" rx="1.4" />
      <rect x="13" y="9.4" width="4.4" height="3.2" rx="1.4" />
      <path d="M9.4 16a3.6 3.6 0 0 0 5.2 0" />
    </>,
    p,
  );
export const IconPlay = ({ size = 22 }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8.5 5.5v13l10-6.5-10-6.5Z" />
  </svg>
);
export const IconTrash = (p: IconProps) =>
  svg(
    <>
      <path d="M5 7.5h14" />
      <path d="M9.5 7.5V5.8h5v1.7" />
      <path d="M6.8 7.5l.8 11.2h8.8l.8-11.2" />
    </>,
    p,
  );

// --- primitives -------------------------------------------------------------

export function Avatar({
  name,
  photoURL,
  size = 40,
  style,
}: {
  name: string;
  photoURL?: string | null;
  size?: number;
  style?: CSSProperties;
}) {
  const initial = (name || '?').trim().charAt(0).toUpperCase();
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38), ...style }}
    >
      {photoURL ? <Photo loading="eager" src={photoURL} alt="" /> : initial}
    </span>
  );
}

/**
 * The rail item from layout H. A filled ring means they have posted today, a
 * dashed one means they have not — the whole point of the rail is that a quiet
 * day is visible at a glance.
 */
export function RingAvatar({
  name,
  photoURL,
  posted,
  you,
  size = 58,
}: {
  name: string;
  photoURL?: string | null;
  posted: boolean;
  you?: boolean;
  size?: number;
}) {
  const cls = posted ? 'ring' : you ? 'ring ring--you' : 'ring ring--empty';
  return (
    <span className={cls} style={{ width: size, height: size }}>
      <Avatar name={name} photoURL={photoURL} size={size} style={{ borderRadius: 'var(--r-pill)' }} />
    </span>
  );
}

export function Chip({
  children,
  tone,
}: {
  children: ReactNode;
  tone?: 'saved' | 'late' | 'accent';
}) {
  return <span className={tone ? `chip chip--${tone}` : 'chip'}>{children}</span>;
}

export function MediaWell({
  height,
  radius = 18,
  children,
}: {
  height: number | string;
  radius?: number;
  children?: ReactNode;
}) {
  return (
    <span
      className="well"
      style={{ display: 'flex', height, borderRadius: radius, position: 'relative' }}
    >
      {children ?? <IconImage size={28} strokeWidth={1.5} />}
    </span>
  );
}

/**
 * The top of a sheet: what it is, how many where that helps, and a way out.
 *
 * The comments sheet's header, shared. Sheets used to open on a bold line of
 * their own and close only by a drag or a tap above them, which read as a
 * panel left half-built.
 */
export function SheetHead({
  title,
  count,
  closeLabel = 'Close',
  onClose,
  children,
}: {
  title: string;
  count?: number;
  closeLabel?: string;
  onClose: () => void;
  /** Anything more for the row, between the count and the close button. */
  children?: ReactNode;
}) {
  return (
    <div className="sheet-head">
      <h2 className="sheet-head-title">{title}</h2>
      {count !== undefined && <span className="sheet-head-count">{count}</span>}
      {children}
      <button
        type="button"
        className="sheet-head-close"
        aria-label={closeLabel}
        // Not the start of a drag: the sheet would otherwise take the press.
        onPointerDown={(event) => event.stopPropagation()}
        onClick={onClose}
      >
        <IconClose size={17} />
      </button>
    </div>
  );
}

/** A drag past this, downward, means put it away. Matches the pager's. */
const SHEET_DISMISS_AT = 78;
/** How long it takes to leave. Kept in step with the stylesheet. */
const SHEET_EXIT_MS = 220;

/**
 * A panel up from the bottom, pushed back down with a finger.
 *
 * The gesture is the sheet's own rather than the browser's, for the reason the
 * pager's comments needed the same treatment: touch-action is one answer per
 * element and an ancestor refusing vertical drags refuses them for its
 * children too, so a sheet cannot decline them and still let its contents
 * scroll. So nothing is declined — every drag arrives here, and where the
 * finger is decides what moves.
 *
 * The scrim captures the pointer, which is what makes a drag that starts above
 * the sheet work: it ends with the finger over the sheet, so without capture
 * the events stop arriving halfway through and the gesture dies. Tapping the
 * scrim still closes it, for anyone who would rather tap.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; owner: 'undecided' | 'sheet' | 'list' } | null>(null);
  const pulled = useRef(0);
  const leaving = useRef(false);
  const [pull, setPull] = useState(0);
  const [going, setGoing] = useState(false);
  // Looked for once it is on the page, still before anything is painted: on
  // the first render the app's frame may not be in the document yet. Nothing
  // is drawn until then, so what is in the sheet mounts once, in one place.
  const [host, setHost] = useState<Element | null>(null);
  useLayoutEffect(() => setHost(document.querySelector('.app') ?? document.body), []);

  const leave = () => {
    if (leaving.current) return;
    leaving.current = true;
    setGoing(true);
    window.setTimeout(onClose, SHEET_EXIT_MS);
  };
  useBackCloses(leave);

  const pullTo = (next: number) => {
    pulled.current = Math.max(0, next);
    setPull(pulled.current);
  };

  const onMove = (event: ReactPointerEvent<HTMLElement>) => {
    const held = drag.current;
    if (!held) return;

    const step = event.clientY - held.y;
    if (step === 0) return;
    held.y = event.clientY;

    const node = box.current;
    const inside = node?.contains(event.target as Node) ?? false;

    if (held.owner === 'undecided') {
      const atTop = (node?.scrollTop ?? 0) <= 0;
      // Down from the top of the contents is the sheet leaving. Anything the
      // contents could honour belongs to the contents.
      held.owner = !inside || (step > 0 && atTop) ? 'sheet' : 'list';
    }

    if (held.owner === 'list' && node) {
      node.scrollTop -= step;
      if (step > 0 && node.scrollTop <= 0) held.owner = 'sheet';
      return;
    }

    pullTo(pulled.current + step);
  };

  const onUp = () => {
    if (!drag.current) return;
    drag.current = null;
    const far = pulled.current > SHEET_DISMISS_AT;
    pullTo(0);
    if (far) leave();
  };

  if (!host) return null;

  // On the app, not the screen that opened it. Inside the screen it stopped at
  // the top of the tab bar, and the camera key, which rises out of the bar,
  // had its top cut off by the sheet's bottom edge.
  return createPortal(
    <>
      <button
        className={going ? 'scrim is-going' : 'scrim'}
        aria-label="Close"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { y: event.clientY, owner: 'sheet' };
        }}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onClick={leave}
        style={{ border: 0, padding: 0 }}
      />
      <div
        ref={box}
        className={going ? 'sheet is-going' : 'sheet'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          transform: pull ? `translateY(${pull}px)` : undefined,
          transition: pull ? 'none' : undefined,
        }}
        onPointerDown={(event) => {
          // Not from a field. A sheet with a form in it is one people type
          // into, and dragging across a word to select it would otherwise
          // throw the whole thing away mid-sentence.
          const from = event.target as HTMLElement;
          if (from.closest('input, textarea, select')) return;
          drag.current = { y: event.clientY, owner: 'undecided' };
        }}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        <span className="grabber" />
        <SheetHead title={title} onClose={leave} />
        {children}
      </div>
    </>,
    host,
  );
}

export interface ViewerItem {
  key: string;
  src: string;
  /** The still a clip shows until it is played. */
  poster?: string;
  /**
   * What stands in while the photo comes: its small copy, usually already on
   * the phone from wherever it was opened, or else its blurred stand-in.
   */
  preview?: string;
  video?: boolean;
  who?: string;
  at?: Date | null;
}

/**
 * One photo or clip full screen, with the rest of its set either side.
 *
 * Takes resolved urls rather than MediaRefs so this file stays clear of the
 * data layer, which is the only reason every screen can import from it.
 */
/** Past this much of a drag, the set moves on rather than springing back. */
const SWIPE_AT = 56;
/** Movement below this is a tap, not a direction. */
const VIEWER_SLOP = 8;
const GLIDE_MS = 200;
/** As far in as a pinch will go. */
const MAX_SCALE = 5;
/** Where a double tap lands when it zooms in. */
const TAP_SCALE = 2.5;
/** Two taps closer together than this are one gesture. */
const DOUBLE_TAP_MS = 280;
/**
 * How much a wheel turns the zoom, per pixel it reports. A mouse notch is a
 * hundred pixels, about a sixth closer; a trackpad pinch reports a few at a
 * time, many times a second, so it gets more for each.
 */
const WHEEL_RATE = 0.0015;
const PINCH_WHEEL_RATE = 0.01;
/** Pulled down further than this, the viewer goes rather than springing back. */
const PULL_CLOSE_PX = 120;
/** Or a flick down this fast, however short, in pixels a millisecond. */
const PULL_CLOSE_SPEED = 0.6;

interface Zoom {
  scale: number;
  x: number;
  y: number;
}

const UNZOOMED: Zoom = { scale: 1, x: 0, y: 0 };

/**
 * Keeps the picture over its own frame. The bound treats the image as filling
 * the stage, which is as much as is known without measuring the drawn size —
 * generous for a letterboxed shot, never tight enough to lose it.
 */
function held(zoom: Zoom, w: number, h: number): Zoom {
  const mx = Math.max(0, ((zoom.scale - 1) * w) / 2);
  const my = Math.max(0, ((zoom.scale - 1) * h) / 2);
  return {
    scale: zoom.scale,
    x: Math.min(mx, Math.max(-mx, zoom.x)),
    y: Math.min(my, Math.max(-my, zoom.y)),
  };
}

export function MediaViewer({
  items,
  index,
  onIndex,
  onClose,
  onSave,
}: {
  items: ViewerItem[];
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
  /** Hands the item at this index to the OS. Absent means no save button. */
  onSave?: (i: number) => void | Promise<void>;
}) {
  useBackCloses(onClose);
  const stage = useRef<HTMLDivElement | null>(null);
  /** Every finger currently down, so a second one can start a pinch. */
  const touches = useRef(new Map<number, { x: number; y: number }>());
  /*
   * One finger's gesture. Sideways it moves through the set; straight down it
   * pulls the picture away to close, which is what a phone's own photos do.
   */
  const drag = useRef<{ x: number; y: number; moved: boolean; pull?: boolean; at: number } | null>(
    null,
  );
  const pinch = useRef<
    { gap: number; mx: number; my: number; from: Zoom } | null
  >(null);
  const timer = useRef<number | undefined>(undefined);
  const lastTap = useRef(0);

  const [dx, setDx] = useState(0);
  /** How far the picture has been pulled down, on its way to closing. */
  const [dy, setDy] = useState(0);
  /** Transition on, which it must not be while a finger is still down. */
  const [gliding, setGliding] = useState(false);
  const [zoom, setZoom] = useState<Zoom>(UNZOOMED);
  const [saving, setSaving] = useState(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);
  // A new picture starts where the last one did, not half way into it.
  useEffect(() => setZoom(UNZOOMED), [index]);

  /*
   * A mouse wheel or a trackpad pinch, on a computer, zooms on the point under
   * the pointer. Chrome reports a pinch as a wheel with ctrlKey and would
   * otherwise zoom the whole page, which is why this is a listener of its own:
   * React's wheel handler is passive and cannot refuse it. Every wheel zooms,
   * because nothing in the viewer scrolls and a drag already pans.
   */
  const showing = Boolean(items[index]);
  useEffect(() => {
    const node = stage.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const z = pageZoom();
      const rect = node.getBoundingClientRect();
      const px = (event.clientX - rect.left) / z;
      const py = (event.clientY - rect.top) / z;
      const w = node.clientWidth || 320;
      const h = node.clientHeight || 480;
      // Firefox counts some wheels in lines rather than pixels.
      const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
      const rate = event.ctrlKey ? PINCH_WHEEL_RATE : WHEEL_RATE;
      setZoom((was) => {
        const scale = Math.min(MAX_SCALE, Math.max(1, was.scale * Math.exp(-delta * rate)));
        if (scale === 1) return UNZOOMED;
        const k = scale / was.scale;
        const cx = w / 2;
        const cy = h / 2;
        // Whatever was under the pointer stays under it, as under a pinch.
        return held({ scale, x: px - cx - (px - cx - was.x) * k, y: py - cy - (py - cy - was.y) * k }, w, h);
      });
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [showing]);

  const item = items[index];
  if (!item) return null;

  // `||` rather than `??`: an element measured before layout reports zero,
  // and a zero here would divide the whole gesture into nonsense.
  const box = () => ({
    w: stage.current?.clientWidth || 320,
    h: stage.current?.clientHeight || 480,
  });

  /**
   * Slides the rest of the way and only then changes the index. The committed
   * layout puts the new picture exactly where the animated one finished, so
   * the swap underneath it is invisible.
   */
  const settle = (step: number) => {
    setGliding(true);
    setDx(step === 0 ? 0 : -step * box().w);
    timer.current = window.setTimeout(() => {
      setGliding(false);
      setDx(0);
      if (step !== 0) onIndex(index + step);
    }, GLIDE_MS);
  };

  /*
   * Where a finger is, in the page's own units. The text size zooms the whole
   * document, so the screen reports a finger in screen pixels while the
   * picture is measured and moved in zoomed ones: undivided, a pan trailed
   * the finger by 15% and a double tap zoomed in short of where it landed.
   */
  const spot = (event: { clientX: number; clientY: number }) => {
    const z = pageZoom();
    return { x: event.clientX / z, y: event.clientY / z };
  };

  const midpoint = () => {
    const [a, b] = [...touches.current.values()];
    return {
      gap: Math.hypot(a.x - b.x, a.y - b.y),
      mx: (a.x + b.x) / 2,
      my: (a.y + b.y) / 2,
    };
  };

  const onPointerDown = (event: ReactPointerEvent) => {
    if ((event.target as HTMLElement).closest('button')) return;
    window.clearTimeout(timer.current);
    setGliding(false);
    const finger = spot(event);
    touches.current.set(event.pointerId, finger);

    if (touches.current.size === 2) {
      // A pinch beats whatever the first finger had started.
      drag.current = null;
      const { gap, mx, my } = midpoint();
      pinch.current = { gap, mx, my, from: zoom };
      return;
    }
    // A clip's own scrubber is a horizontal drag too, and it was there first.
    if (touches.current.size === 1 && !item.video) {
      drag.current = { ...finger, moved: false, at: Date.now() };
    }
  };

  const onPointerMove = (event: ReactPointerEvent) => {
    if (!touches.current.has(event.pointerId)) return;
    const finger = spot(event);
    touches.current.set(event.pointerId, finger);

    const holding = pinch.current;
    if (holding && touches.current.size >= 2) {
      const { gap, mx, my } = midpoint();
      const scale = Math.min(MAX_SCALE, Math.max(1, (holding.from.scale * gap) / holding.gap));
      const { w, h } = box();
      const cx = w / 2;
      const cy = h / 2;
      const k = scale / holding.from.scale;

      // Whatever was under the midpoint stays under it, and follows it.
      setZoom(
        held(
          {
            scale,
            x: mx - cx - (holding.mx - cx - holding.from.x) * k,
            y: my - cy - (holding.my - cy - holding.from.y) * k,
          },
          w,
          h,
        ),
      );
      return;
    }

    const current = drag.current;
    if (!current) return;
    const mx = finger.x - current.x;
    const my = finger.y - current.y;

    if (zoom.scale > 1) {
      // Zoomed in, a finger moves the picture rather than the set.
      if (!current.moved && Math.abs(mx) < VIEWER_SLOP && Math.abs(my) < VIEWER_SLOP) return;
      current.moved = true;
      current.x = finger.x;
      current.y = finger.y;
      const { w, h } = box();
      setZoom((was) => held({ scale: was.scale, x: was.x + mx, y: was.y + my }, w, h));
      return;
    }

    if (!current.moved) {
      if (Math.abs(mx) < VIEWER_SLOP && Math.abs(my) < VIEWER_SLOP) return;
      // Up or down pulls; only down carries the picture, and only down closes.
      if (Math.abs(my) > Math.abs(mx)) current.pull = true;
      current.moved = true;
    }

    if (current.pull) {
      setDy(Math.max(0, my));
      return;
    }

    // The ends resist rather than give, so the set feels like it has edges.
    const stuck = (mx > 0 && index === 0) || (mx < 0 && index >= items.length - 1);
    setDx(stuck ? mx / 4 : mx);
  };

  /** Toggles between fitting the screen and a closer look at the point tapped. */
  const toggleZoom = (px: number, py: number) => {
    if (zoom.scale > 1) return setZoom(UNZOOMED);
    const { w, h } = box();
    const cx = w / 2;
    const cy = h / 2;
    setZoom(
      held(
        {
          scale: TAP_SCALE,
          x: -(px - cx) * (TAP_SCALE - 1),
          y: -(py - cy) * (TAP_SCALE - 1),
        },
        w,
        h,
      ),
    );
  };

  const onPointerUp = (event: ReactPointerEvent) => {
    touches.current.delete(event.pointerId);

    if (pinch.current) {
      if (touches.current.size >= 2) return;
      pinch.current = null;
      // Anything short of a real zoom springs back to the frame.
      if (zoom.scale <= 1.02) setZoom(UNZOOMED);
      drag.current = null;
      return;
    }

    const current = drag.current;
    drag.current = null;

    if (current?.pull) {
      const down = spot(event).y - current.y;
      const speed = down / Math.max(1, Date.now() - current.at);
      if (down > PULL_CLOSE_PX || speed > PULL_CLOSE_SPEED) return onClose();
      // Short of it, back up to where it sat.
      setGliding(true);
      setDy(0);
      timer.current = window.setTimeout(() => setGliding(false), GLIDE_MS);
      return;
    }

    if (current?.moved) {
      if (zoom.scale > 1) return; // It was a pan; the picture is where it is.
      if (dx <= -SWIPE_AT && index < items.length - 1) settle(1);
      else if (dx >= SWIPE_AT && index > 0) settle(-1);
      else settle(0);
      return;
    }

    const target = event.target as HTMLElement;
    // Buttons run their own onClick; letting this through would close the
    // viewer instead of working the arrows.
    if (target.closest('button')) return;

    // A tap that misses the picture closes it, which is what a tap outside a
    // lightbox has always meant. On the picture it is half of a double tap,
    // and a double tap is what zooms.
    if (!target.closest('[data-viewer-media]')) return onClose();

    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_MS) {
      lastTap.current = 0;
      const rect = stage.current?.getBoundingClientRect();
      const finger = spot(event);
      const corner = spot({ clientX: rect?.left ?? 0, clientY: rect?.top ?? 0 });
      toggleZoom(finger.x - corner.x, finger.y - corner.y);
    } else {
      lastTap.current = now;
    }
  };

  const onPointerCancel = (event: ReactPointerEvent) => {
    touches.current.delete(event.pointerId);
    pinch.current = null;
    drag.current = null;
    setDy(0);
    if (zoom.scale === 1) settle(0);
  };

  // The room behind clears as the picture is pulled, so it reads as leaving.
  const behind = Math.max(0, 1 - dy / 400);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo"
      // Its ground is near-black in either theme, so what is written on it is too.
      className="media-dark"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: `rgba(8,9,12,${(0.97 * behind).toFixed(3)})`,
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <div className="row" style={{ padding: 14, gap: 10, alignItems: 'center' }}>
        <span className="col grow" style={{ alignItems: 'flex-start', gap: 2 }}>
          <span className="small bold">{item.who || 'Someone'}</span>
          <span className="tiny dim">
            {item.at
              ? item.at.toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : ''}
          </span>
        </span>
        {/* The way in that can be seen. A double tap, a pinch and a wheel all
            work too, but nothing on screen said so. */}
        <button
          className="icon-btn"
          aria-label={zoom.scale > 1 ? 'Zoom out' : 'Zoom in'}
          onClick={() => {
            const { w, h } = box();
            toggleZoom(w / 2, h / 2);
          }}
        >
          {zoom.scale > 1 ? <IconZoomOut size={20} /> : <IconZoomIn size={20} />}
        </button>
        {onSave && (
          <button
            className="icon-btn"
            aria-label="Save to phone"
            disabled={saving}
            onClick={() => {
              setSaving(true);
              void Promise.resolve(onSave(index))
                .catch(() => undefined)
                .finally(() => setSaving(false));
            }}
          >
            {saving ? <Spinner /> : <IconDownload size={20} />}
          </button>
        )}
        <button className="icon-btn" aria-label="Close" onClick={onClose}>
          <IconClose size={20} />
        </button>
      </div>

      {/* Only the neighbours are drawn. The gallery's set runs to hundreds,
          and three is all a swipe can ever show. */}
      <div
        ref={stage}
        style={{ position: 'relative', overflow: 'hidden', touchAction: 'none' }}
      >
        {[-1, 0, 1].map((offset) => {
          const near = items[index + offset];
          if (!near) return null;
          const here = offset === 0;

          return (
            <div
              key={near.key}
              className="center"
              style={{
                position: 'absolute',
                inset: 0,
                padding: '0 8px',
                transform: `translateX(calc(${offset * 100}% + ${dx}px))`,
                transition: gliding ? `transform ${GLIDE_MS}ms ease-out` : 'none',
              }}
            >
              <span
                className="center"
                style={{
                  // Only the one being looked at carries the zoom; its
                  // neighbours stay at rest waiting their turn.
                  // A pull carries it down and shrinks it a little as it goes.
                  transform: here
                    ? `translate(${zoom.x}px, ${zoom.y + dy}px) scale(${zoom.scale * (1 - Math.min(dy, 400) / 2000)})`
                    : undefined,
                  transition:
                    here && (gliding || (!pinch.current && !drag.current))
                      ? 'transform 160ms ease-out'
                      : 'none',
                  // The whole slide, not the picture's size. A percentage
                  // height measured against a box sized by its contents means
                  // nothing in Chrome, so a portrait photo ignored its
                  // max-height below and ran off the bottom of the screen.
                  width: '100%',
                  height: '100%',
                }}
              >
                {near.video ? (
                  <video
                    data-viewer-media=""
                    src={near.src}
                    poster={near.poster}
                    controls
                    playsInline
                    style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 'var(--r-sm)' }}
                  />
                ) : (
                  <Photo loading="eager"
                    data-viewer-media=""
                    src={near.src}
                    preview={near.preview}
                    alt=""
                    draggable={false}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 'var(--r-sm)',
                    }}
                  />
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className="row" style={{ padding: 16, gap: 10, alignItems: 'center' }}>
        <button
          className="icon-btn"
          aria-label="Previous"
          disabled={index === 0}
          onClick={() => onIndex(index - 1)}
        >
          <IconBack size={20} />
        </button>
        <span className="tiny dim grow" style={{ textAlign: 'center' }}>
          {index + 1} of {items.length}
        </span>
        <button
          className="icon-btn"
          aria-label="Next"
          disabled={index >= items.length - 1}
          onClick={() => onIndex(index + 1)}
        >
          <IconForward size={20} />
        </button>
      </div>
    </div>
  );
}

/**
 * The aperture, opening and closing, wherever something is being waited on.
 *
 * The same shutter the app starts on, reduced to the part that survives being
 * small. The whole mark does not: at eighteen pixels the letter is a smudge,
 * and the blue tile behind it would sit on top of a blue button looking like
 * a hole in it. The iris alone is legible at any size, and drawn in
 * currentColor it takes the colour of whatever it is waiting inside -- white
 * on an accent button, dim beside a line of grey text.
 *
 * It breathes rather than snapping like the startup one. A shutter firing is
 * an event and wants a pause after it; this is a thing still going on, and a
 * pause in the middle of it reads as having stopped.
 */
export function Spinner() {
  return (
    <svg
      className="aperture"
      viewBox="0 0 136 136"
      role="status"
      aria-label="Working"
      focusable="false"
    >
      <g transform="translate(68 68)">
        <path
          className="aperture-iris"
          d="M0 -58 L50.2 -29 L50.2 29 L0 58 L-50.2 29 L-50.2 -29 Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="14"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

/** The quick row. Everything else comes from the keyboard's emoji key. */
export const COMMON_REACTIONS = ['❤️', '😂', '👍', '😮', '😢', '🔥', '🙏', '😍'];


/**
 * The whole set the plus button opens, grouped the way a keyboard groups them.
 *
 * Hand-held rather than pulled from a library: the full Unicode set with its
 * search keywords is megabytes, and a family of eleven reaching for a reaction
 * wants the ones people actually use, on one screen, not all six thousand.
 *
 * There is no web API that opens the system emoji keyboard — `inputmode` has
 * no value for it — so focusing a hidden field, which is what this replaced,
 * could only ever raise the ordinary keyboard and leave you to find the smiley
 * key yourself.
 */
/**
 * What each one is called, for the search field.
 *
 * Written here rather than pulled from a package: the full Unicode name and
 * keyword set is megabytes, most of it for emoji this picker does not carry,
 * and what a family actually types is "party", "thumbs", "crying" -- the
 * everyday word, not the standard's. Several carry more than one because more
 * than one is right: fries and chips, donut and doughnut, soccer and football.
 */
const EMOJI_WORDS: Record<string, string> = {
  '😀': 'grin smile happy',
  '😃': 'smile happy open',
  '😄': 'grin happy laugh',
  '😁': 'beam grin teeth',
  '😆': 'laugh squint haha',
  '😅': 'sweat laugh relief phew',
  '🤣': 'rofl laugh floor howling',
  '😂': 'joy laugh cry tears funny',
  '🙂': 'slight smile',
  '🙃': 'upside down silly',
  '😉': 'wink',
  '😊': 'blush smile happy',
  '😇': 'halo angel innocent',
  '🥰': 'love hearts adore',
  '😍': 'heart eyes love',
  '🤩': 'star struck wow amazed',
  '😘': 'kiss blow love',
  '😋': 'yum tongue tasty delicious',
  '😛': 'tongue cheeky',
  '😜': 'wink tongue silly',
  '🤪': 'zany crazy wild',
  '🤗': 'hug hugging',
  '🤭': 'oops giggle hand',
  '🤫': 'shush quiet secret',
  '🤔': 'think thinking hmm',
  '😐': 'neutral blank',
  '😑': 'expressionless meh',
  '😶': 'no mouth silent',
  '😏': 'smirk',
  '😒': 'unamused meh annoyed',
  '🙄': 'eyeroll roll eyes',
  '😬': 'grimace awkward yikes',
  '😌': 'relieved calm content',
  '😔': 'sad pensive down',
  '😪': 'sleepy tired',
  '😴': 'sleep sleeping zzz',
  '🤒': 'sick thermometer ill',
  '🤕': 'hurt bandage injured',
  '🤢': 'sick nauseated gross',
  '🥵': 'hot heat sweating',
  '🥶': 'cold freezing',
  '🥴': 'woozy dizzy drunk',
  '😵': 'dizzy knocked out',
  '🤯': 'mind blown exploding',
  '🤠': 'cowboy hat',
  '😎': 'cool sunglasses',
  '🤓': 'nerd glasses geek',
  '🧐': 'monocle inspect',
  '😕': 'confused',
  '😟': 'worried',
  '🙁': 'frown sad',
  '😮': 'open mouth surprise wow',
  '😯': 'hushed surprise',
  '😲': 'astonished shocked',
  '😳': 'flushed embarrassed',
  '🥺': 'pleading puppy beg',
  '😦': 'frowning open',
  '😨': 'fearful scared',
  '😰': 'anxious sweat worried',
  '😢': 'cry sad tear',
  '😭': 'sob cry bawling',
  '😱': 'scream fear shock',
  '😖': 'confounded',
  '😞': 'disappointed sad',
  '😩': 'weary tired',
  '😫': 'tired exhausted',
  '🥱': 'yawn bored',
  '😤': 'triumph huff steam',
  '😡': 'angry mad rage',
  '🤬': 'swear curse angry',
  '😈': 'devil imp mischief',
  '💀': 'skull dead death',
  '💩': 'poo poop',
  '🤡': 'clown',
  '👍': 'thumbs up yes like good',
  '👎': 'thumbs down no dislike',
  '👌': 'ok okay perfect',
  '🤌': 'pinched fingers italian',
  '🤏': 'pinch small tiny',
  '✌️': 'peace victory',
  '🤞': 'fingers crossed luck',
  '🫰': 'fingers heart money snap',
  '🤟': 'love you',
  '🤘': 'rock horns metal',
  '🤙': 'call me shaka hang loose',
  '👈': 'point left',
  '👉': 'point right',
  '👆': 'point up',
  '👇': 'point down',
  '☝️': 'point up index',
  '✋': 'hand stop raised',
  '🤚': 'back of hand raised',
  '🖐️': 'hand fingers splayed',
  '🖖': 'vulcan spock',
  '👋': 'wave hello hi bye',
  '🤝': 'handshake deal agree',
  '🙏': 'pray thanks please namaste',
  '✍️': 'write writing hand',
  '💪': 'muscle strong flex',
  '🫶': 'heart hands love',
  '🤲': 'palms up open',
  '👏': 'clap applause',
  '🙌': 'raise hands praise celebrate',
  '🫡': 'salute saluting',
  '❤️': 'red heart love',
  '🧡': 'orange heart',
  '💛': 'yellow heart',
  '💚': 'green heart',
  '💙': 'blue heart',
  '💜': 'purple heart',
  '🖤': 'black heart',
  '🤍': 'white heart',
  '🤎': 'brown heart',
  '💔': 'broken heart break',
  '❣️': 'heart exclamation',
  '💕': 'two hearts love',
  '💞': 'revolving hearts',
  '💓': 'beating heart',
  '💗': 'growing heart',
  '💖': 'sparkling heart',
  '💘': 'heart arrow cupid',
  '💝': 'heart ribbon gift',
  '💟': 'heart decoration',
  '💌': 'love letter',
  '🎉': 'party popper celebrate tada',
  '🎊': 'confetti ball party',
  '🥳': 'party face celebrate hat',
  '🎈': 'balloon',
  '🎁': 'gift present',
  '🎂': 'birthday cake',
  '🍰': 'cake slice',
  '🧁': 'cupcake',
  '🎄': 'christmas tree',
  '🎃': 'halloween pumpkin jack',
  '🦃': 'turkey thanksgiving',
  '🎆': 'fireworks',
  '🎇': 'sparkler firework',
  '✨': 'sparkles shine shiny',
  '⭐': 'star',
  '🌟': 'glowing star',
  '💫': 'dizzy star',
  '🔥': 'fire lit hot flame',
  '💯': 'hundred perfect score',
  '🏆': 'trophy win winner',
  '🥇': 'gold medal first',
  '🎯': 'target bullseye dart',
  '🔔': 'bell',
  '🎵': 'music note',
  '🎶': 'music notes song',
  '🎤': 'microphone sing karaoke',
  '🎧': 'headphones',
  '📸': 'camera photo flash',
  '🕯️': 'candle',
  '💐': 'bouquet flowers',
  '🐶': 'dog puppy',
  '🐱': 'cat kitten',
  '🐭': 'mouse',
  '🐹': 'hamster',
  '🐰': 'rabbit bunny',
  '🦊': 'fox',
  '🐻': 'bear',
  '🐼': 'panda',
  '🐨': 'koala',
  '🐯': 'tiger',
  '🦁': 'lion',
  '🐮': 'cow',
  '🐷': 'pig',
  '🐸': 'frog',
  '🐵': 'monkey',
  '🙈': 'see no evil monkey',
  '🙉': 'hear no evil monkey',
  '🙊': 'speak no evil monkey',
  '🦄': 'unicorn',
  '🐝': 'bee',
  '🦋': 'butterfly',
  '🐞': 'ladybug beetle',
  '🐢': 'turtle',
  '🐠': 'fish tropical',
  '🐳': 'whale',
  '🌸': 'blossom cherry flower',
  '🌺': 'hibiscus flower',
  '🌻': 'sunflower',
  '🌷': 'tulip flower',
  '🌹': 'rose flower',
  '🌲': 'tree evergreen',
  '🌴': 'palm tree',
  '🌵': 'cactus',
  '🍀': 'clover luck shamrock',
  '🍁': 'maple leaf autumn fall',
  '🌈': 'rainbow',
  '☀️': 'sun sunny',
  '🌙': 'moon crescent night',
  '⛄': 'snowman snow winter',
  '🌊': 'wave ocean water sea',
  '🍕': 'pizza',
  '🍔': 'burger hamburger',
  '🍟': 'fries chips',
  '🌭': 'hotdog',
  '🌮': 'taco',
  '🌯': 'burrito wrap',
  '🥗': 'salad',
  '🍝': 'pasta spaghetti',
  '🍜': 'ramen noodles soup',
  '🍣': 'sushi',
  '🍤': 'shrimp prawn',
  '🍚': 'rice',
  '🥐': 'croissant',
  '🥖': 'baguette bread',
  '🧀': 'cheese',
  '🥓': 'bacon',
  '🍳': 'egg fried breakfast',
  '🥞': 'pancakes',
  '🍩': 'donut doughnut',
  '🍪': 'cookie biscuit',
  '🍫': 'chocolate',
  '🍬': 'candy sweet',
  '🍭': 'lollipop',
  '🍦': 'ice cream',
  '🍎': 'apple',
  '🍌': 'banana',
  '🍉': 'watermelon',
  '🍇': 'grapes',
  '🍓': 'strawberry',
  '🥑': 'avocado',
  '🥕': 'carrot',
  '🌽': 'corn',
  '☕': 'coffee hot drink',
  '🍵': 'tea green',
  '🥤': 'soda drink cup',
  '🍺': 'beer',
  '🥂': 'cheers champagne toast',
  '🍷': 'wine',
  '🧊': 'ice cube',
  '🍿': 'popcorn',
  '⚽': 'soccer football',
  '🏀': 'basketball',
  '🏈': 'football american',
  '⚾': 'baseball',
  '🎾': 'tennis',
  '🏐': 'volleyball',
  '🏉': 'rugby',
  '🎱': 'pool billiards eight ball',
  '🏓': 'ping pong table tennis',
  '🏸': 'badminton',
  '⛳': 'golf',
  '🏹': 'archery bow arrow',
  '🎣': 'fishing',
  '🥊': 'boxing glove',
  '🎿': 'ski skiing',
  '🏂': 'snowboard',
  '🏄': 'surf surfing',
  '🚴': 'bike cycling',
  '🏃': 'run running',
  '🧗': 'climb climbing',
  '🏋️': 'lift weights gym',
  '🤸': 'cartwheel gymnastics',
  '💃': 'dance dancer',
  '🕺': 'dance man dancing',
  '🚗': 'car',
  '🚕': 'taxi cab',
  '🚙': 'suv car',
  '🚌': 'bus',
  '🏍️': 'motorcycle',
  '✈️': 'plane flight travel',
  '🚀': 'rocket space launch',
  '⛵': 'sailboat boat sailing',
  '🏠': 'house home',
  '🏔️': 'mountain',
  '🏖️': 'beach',
  '🗺️': 'map',
  '🧳': 'luggage suitcase travel',
  '⛺': 'tent camping',
  '🎢': 'roller coaster',
  '🎡': 'ferris wheel',
  '📱': 'phone mobile',
  '💻': 'laptop computer',
  '⌚': 'watch',
  '📷': 'camera',
  '🎮': 'game controller gaming',
  '🕹️': 'joystick arcade',
  '🧩': 'puzzle piece',
  '🎲': 'dice game',
  '📚': 'books reading',
  '✏️': 'pencil write',
  '💡': 'idea bulb light',
  '🔑': 'key',
  '🔒': 'lock locked',
  '💰': 'money bag',
  '💵': 'dollar cash money',
  '🎓': 'graduation school degree',
  '⚙️': 'settings gear',
  '🔧': 'wrench tool fix',
  '🧲': 'magnet',
  '⏰': 'alarm clock time',
  '📌': 'pin pushpin',
  '📎': 'paperclip',
  '✅': 'check tick done yes',
  '❌': 'cross no wrong',
  '⚠️': 'warning caution',
  '❓': 'question',
  '❗': 'exclamation',
  '💤': 'sleep zzz',
  '🆒': 'cool',
  '🔝': 'top',
};

interface EmojiGroup {
  name: string;
  tab: string;
  list: string[];
}

/** The ones chosen by hand, first in each group because they are the ones used. */
const HAND_PICKED: EmojiGroup[] = [
  {
    name: 'Smileys',
    tab: '\u{1F600}',
    list: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😋', '😛', '😜',
      '🤪', '🤗', '🤭', '🤫', '🤔', '😐', '😑', '😶', '😏', '😒',
      '🙄', '😬', '😌', '😔', '😪', '😴', '🤒', '🤕', '🤢', '🥵',
      '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕',
      '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦', '😨', '😰',
      '😢', '😭', '😱', '😖', '😞', '😩', '😫', '🥱', '😤', '😡',
      '🤬', '😈', '💀', '💩', '🤡',
    ],
  },
  {
    name: 'Hands',
    tab: '\u{1F44D}',
    list: [
      '👍', '👎', '👌', '🤌', '🤏', '✌️', '🤞', '🫰', '🤟', '🤘',
      '🤙', '👈', '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖',
      '👋', '🤝', '🙏', '✍️', '💪', '🫶', '🤲', '👏', '🙌', '🫡',
    ],
  },
  {
    name: 'Hearts',
    tab: '\u2764\uFE0F',
    list: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '💌',
    ],
  },
  {
    name: 'Celebration',
    tab: '\u{1F389}',
    list: [
      '🎉', '🎊', '🥳', '🎈', '🎁', '🎂', '🍰', '🧁', '🎄', '🎃',
      '🦃', '🎆', '🎇', '✨', '⭐', '🌟', '💫', '🔥', '💯', '🏆',
      '🥇', '🎯', '🔔', '🎵', '🎶', '🎤', '🎧', '📸', '🕯️', '💐',
    ],
  },
  {
    name: 'Animals and nature',
    tab: '\u{1F436}',
    list: [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
      '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🦄', '🐝',
      '🦋', '🐞', '🐢', '🐠', '🐳', '🌸', '🌺', '🌻', '🌷', '🌹',
      '🌲', '🌴', '🌵', '🍀', '🍁', '🌈', '☀️', '🌙', '⛄', '🌊',
    ],
  },
  {
    name: 'Food',
    tab: '\u{1F355}',
    list: [
      '🍕', '🍔', '🍟', '🌭', '🌮', '🌯', '🥗', '🍝', '🍜', '🍣',
      '🍤', '🍚', '🥐', '🥖', '🧀', '🥓', '🍳', '🥞', '🍩', '🍪',
      '🍫', '🍬', '🍭', '🍦', '🍎', '🍌', '🍉', '🍇', '🍓', '🥑',
      '🥕', '🌽', '☕', '🍵', '🥤', '🍺', '🥂', '🍷', '🧊', '🍿',
    ],
  },
  {
    name: 'Activity and travel',
    tab: '\u26BD',
    list: [
      '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸',
      '⛳', '🏹', '🎣', '🥊', '🎿', '🏂', '🏄', '🚴', '🏃', '🧗',
      '🏋️', '🤸', '💃', '🕺', '🚗', '🚕', '🚙', '🚌', '🏍️', '✈️',
      '🚀', '⛵', '🏠', '🏔️', '🏖️', '🗺️', '🧳', '⛺', '🎢', '🎡',
    ],
  },
  {
    name: 'Things',
    tab: '\u{1F4F1}',
    list: [
      '📱', '💻', '⌚', '📷', '🎮', '🕹️', '🧩', '🎲', '📚', '✏️',
      '💡', '🔑', '🔒', '💰', '💵', '🎓', '⚙️', '🔧', '🧲', '⏰',
      '📌', '📎', '✅', '❌', '⚠️', '❓', '❗', '💤', '🆒', '🔝',
    ],
  },
];

/** Groups the hand-picked set has no counterpart for, and where they go. */
const NEW_GROUPS: { name: string; tab: string; after: string }[] = [
  { name: 'People', tab: '\u{1F9D1}', after: 'Hearts' },
  { name: 'Symbols', tab: '\u{1F523}', after: 'Things' },
  { name: 'Flags', tab: '\u{1F3C1}', after: 'Symbols' },
];

/**
 * Every emoji up to Emoji 15.1: the hand-picked ones first in each group, then
 * the rest of Unicode's set sorted in behind them, then the groups the hand-
 * picked set never had. Generated into emojiMore.ts rather than hand-written,
 * because nobody is going to write words for two thousand emoji.
 */
export interface AllEmoji {
  groups: EmojiGroup[];
  /** Unicode's words for every emoji, behind the hand-written ones. */
  words: Record<string, string>;
}

export function mergeEmoji(more: Record<string, string[]>, words: Record<string, string>): AllEmoji {
  const groups = HAND_PICKED.map((each) => ({
    ...each,
    list: [...each.list, ...(more[each.name] ?? [])],
  }));
  for (const extra of NEW_GROUPS) {
    const at = groups.findIndex((each) => each.name === extra.after);
    groups.splice(at + 1, 0, { name: extra.name, tab: extra.tab, list: more[extra.name] ?? [] });
  }
  return { groups, words };
}

/** The hand-picked set alone: what the picker has until the rest arrives. */
export const HAND_PICKED_EMOJI: AllEmoji = mergeEmoji({}, {});

/*
 * The rest -- two thousand emoji and their words, the largest single piece
 * of the app -- fetched the first time the picker opens rather than with
 * the app, which only ever needed it there. Once, and then kept.
 */
let allEmoji: Promise<AllEmoji> | null = null;
export function loadAllEmoji(): Promise<AllEmoji> {
  allEmoji ??= import('./emojiMore').then((more) => mergeEmoji(more.MORE_EMOJI, more.MORE_WORDS));
  return allEmoji;
}

/** Everything an emoji can be found by: the hand-written words, then Unicode's. */
function wordsOf(emoji: string, all: AllEmoji): string {
  return `${EMOJI_WORDS[emoji] ?? ''} ${all.words[emoji] ?? ''}`.trim();
}

/** Shown for a `true` left over from when chat reactions were heart-only. */
export const LEGACY_REACTION = '❤️';

/**
 * One person's reactions, whatever shape the stored value is in: a list now, a
 * bare emoji from the one-each version, `true` from the heart-only one.
 */
export function emojisOf(value: string[] | string | true | undefined): string[] {
  if (value === undefined) return [];
  if (value === true) return [LEGACY_REACTION];
  return typeof value === 'string' ? [value] : value;
}

/**
 * One entry per distinct emoji, most used first, across everyone who reacted.
 * Chat keeps its own copy of this for message likes; worth unifying when both
 * are not being edited at once.
 */
export function tallyEmoji(
  byPerson: Record<string, string[] | string | true> | undefined,
): { emoji: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const value of Object.values(byPerson ?? {})) {
    for (const emoji of emojisOf(value)) {
      counts.set(emoji, (counts.get(emoji) ?? 0) + 1);
    }
  }
  return [...counts]
    .map(([emoji, count]) => ({ emoji, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Who reacted, rather than only how many did.
 *
 * Grouped the same way the chips are, and in the same order, so the panel
 * reads as an expansion of what was already on screen rather than a second
 * arrangement of it.
 */
export function peopleByEmoji(
  byPerson: Record<string, string[] | string | true> | undefined,
): { emoji: string; uids: string[] }[] {
  const groups = new Map<string, string[]>();

  for (const [uid, value] of Object.entries(byPerson ?? {})) {
    for (const emoji of emojisOf(value)) {
      groups.set(emoji, [...(groups.get(emoji) ?? []), uid]);
    }
  }

  return [...groups]
    .map(([emoji, uids]) => ({ emoji, uids }))
    .sort((a, b) => b.uids.length - a.uids.length);
}

/**
 * A press that outlasts a tap.
 *
 * The chip it goes on already answers a tap by adding or removing your own
 * reaction, so the hold has to be able to say that it happened: `consumed`
 * is asked once by the click handler, which drops the click that a finger
 * lifting off always sends afterwards.
 */
export function useHold(onHold: (at: DOMRect) => void, ms = 420) {
  const timer = useRef<number | undefined>(undefined);
  const fired = useRef(false);

  const stop = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  };

  useEffect(() => stop, []);

  return {
    consumed: () => {
      if (!fired.current) return false;
      fired.current = false;
      return true;
    },
    handlers: {
      onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
        // Kept now rather than read in the timeout: currentTarget is only the
        // element while the event is being handled, and this is read later.
        const pressed = event.currentTarget;
        fired.current = false;
        stop();
        timer.current = window.setTimeout(() => {
          fired.current = true;
          onHold(pressed.getBoundingClientRect());
        }, ms);
      },
      onPointerUp: stop,
      onPointerLeave: stop,
      onPointerCancel: stop,
      // Otherwise a long press on a phone offers to copy the emoji instead.
      onContextMenu: (event: { preventDefault: () => void }) => event.preventDefault(),
    },
  };
}

const PANEL_GAP = 6;
const EDGE = 10;

/**
 * Right under the chip that was held, hanging from the side it is on.
 *
 * Fixed rather than absolute, because the two places this is used sit in very
 * different boxes -- a scrolling thread and a full-screen story -- and an
 * absolute panel would be clipped by one of them. The cost is that it has to
 * be placed by hand, and that it goes stale if the page moves underneath it,
 * which is why the panel closes on a scroll.
 *
 * Always below. It used to open upwards from a chip in the lower half of the
 * screen, and with a chip on the right it was held to the screen's edge
 * rather than to the chip -- so on your own messages, which sit on the right
 * and mostly low down, it turned up somewhere above and to one side. Carter:
 * "it should just be right below the reaction itself". A chip on the left has
 * the panel's left edge under its own; one on the right, its right edge, so
 * the panel grows away from the side it belongs to. ReactedBy lifts it back
 * on screen once drawn, if the chip was too near the bottom for it to fit.
 *
 * In the page's own units, not the screen's. The text size zooms the whole
 * document, so the chip is measured in screen pixels but the panel is placed
 * in zoomed ones: undivided, every distance came out 15% short.
 */
export function placeBelow(at: DOMRect, zoom = pageZoom()): CSSProperties {
  const width = window.innerWidth / zoom;
  const right = at.right ?? at.left;
  const top = at.bottom / zoom + PANEL_GAP;
  const onRight = (at.left + right) / 2 > window.innerWidth / 2;

  return onRight
    ? { right: Math.max(EDGE, width - right / zoom), top }
    : { left: Math.max(EDGE, at.left / zoom), top };
}

/** The text size's zoom on the document, 1 where there is none. */
function pageZoom(): number {
  return parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
}

/** The panel that hold opens: every reaction on the thing, and who left it. */
export function ReactedBy({
  byPerson,
  nameOf,
  at,
  onClose,
}: {
  byPerson: Record<string, string[] | string | true> | undefined;
  nameOf: (uid: string) => string;
  /** Where the held chip was, which is what the panel sits beside. */
  at: DOMRect;
  onClose: () => void;
}) {
  const groups = peopleByEmoji(byPerson);

  // Placed against the viewport, so anything that moves the page leaves it
  // pointing at nowhere. Closing is both simpler and less wrong than chasing.
  //
  // Subscribed once and reached through a ref, because the callers pass a new
  // closure on every render and the story screen renders on a timer -- the
  // plain version took both listeners off and put them back several times a
  // second for as long as the panel was open.
  const close = useRef(onClose);
  close.current = onClose;
  useBackCloses(onClose, groups.length > 0);

  useEffect(() => {
    const go = () => close.current();
    window.addEventListener('scroll', go, true);
    window.addEventListener('resize', go);
    return () => {
      window.removeEventListener('scroll', go, true);
      window.removeEventListener('resize', go);
    };
  }, []);

  /*
   * Below the chip, unless that runs off the foot of the screen -- the newest
   * messages sit just over the composer -- in which case it is lifted only as
   * far as it has to be. Measured once drawn and moved before it is painted,
   * since how tall it is depends on how many reacted.
   */
  const panel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = panel.current;
    if (!node) return;
    const zoom = pageZoom();
    const over = node.getBoundingClientRect().bottom - (window.innerHeight - EDGE * zoom);
    if (over > 0) node.style.top = `${Math.max(EDGE, parseFloat(node.style.top) - over / zoom)}px`;
  }, [at]);

  if (!groups.length) return null;

  // On the page itself, not inside the row it was opened from. A held message
  // is scaled and a swiped one slid, and either gives the row a transform --
  // which makes it the box `position: fixed` measures from, so the panel,
  // placed by the screen's numbers, landed by the row's, in mid-screen.
  return createPortal(
    <>
      <span className="reacted-scrim" onPointerDown={onClose} aria-hidden="true" />
      <div
        className="reacted-by"
        ref={panel}
        style={placeBelow(at)}
        role="dialog"
        aria-label="Who reacted"
      >
        {groups.map(({ emoji, uids }) => (
          <div key={emoji} className="reacted-group">
            <span className="reacted-emoji">{emoji}</span>
            <span className="reacted-names">
              {uids.map((uid) => (
                <span key={uid}>{nameOf(uid)}</span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </>,
    document.body,
  );
}

/**
 * A row of common reactions plus a way to reach every other emoji.
 *
 * There is no web API that opens the emoji keyboard on its own, so the plus
 * focuses an off-screen input: iOS raises the keyboard and the emoji key is
 * one tap away. The input is controlled and always empty, so each pick starts
 * from a clean field and a second reaction works without clearing anything.
 */
/**
 * The emoji whose words include what was typed.
 *
 * A group's own name counts as one of its words, which is what makes "food"
 * and "hands" answer with the whole group. Matching a group and matching a
 * word can both offer the same emoji, so the set at the end is not decoration.
 */
export function searchEmoji(query: string, all: AllEmoji): string[] {
  const asked = query.trim().toLowerCase();
  if (!asked) return [];

  const found = all.groups.flatMap((each) =>
    each.name.toLowerCase().includes(asked)
      ? each.list
      : each.list.filter((emoji) => wordsOf(emoji, all).includes(asked)),
  );
  return [...new Set(found)];
}

/**
 * The whole set, on a panel of its own. Fixed rather than tucked into the bar
 * it opens from: that bar is a pill a few rows deep in chat and a strip over a
 * photo in a story, and neither has room for eight rows of emoji.
 */
export function EmojiPicker({
  onPick,
  onClose,
  over = false,
}: {
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Opened from a sheet, so it has to sit above the sheet rather than under it. */
  over?: boolean;
}) {
  useBackCloses(onClose);
  const [group, setGroup] = useState(0);
  const [query, setQuery] = useState('');
  // The hand-picked ones at once, the rest a moment later: see loadAllEmoji.
  const [all, setAll] = useState<AllEmoji>(HAND_PICKED_EMOJI);
  useEffect(() => {
    let live = true;
    void loadAllEmoji()
      .then((loaded) => live && setAll(loaded))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  // Searching looks at the whole set at once, so the tabs have nothing to say
  // while it is going on and step aside.
  const found = query.trim() ? searchEmoji(query, all) : null;
  const shown = found ?? all.groups[group].list;

  return (
    <>
      <button className={over ? 'emoji-scrim is-over' : 'emoji-scrim'} aria-label="Close" onClick={onClose} />
      <div
        className={over ? 'emoji-panel is-over' : 'emoji-panel'}
        role="dialog"
        aria-modal="true"
        aria-label="Pick an emoji"
      >
        <SheetHead title="All emoji" closeLabel="Close emoji" onClose={onClose} />
        <input
          className="emoji-search"
          type="search"
          value={query}
          placeholder="Search emoji"
          aria-label="Search emoji"
          onChange={(event) => setQuery(event.target.value)}
        />

        {!found && (
          <div className="emoji-tabs" role="tablist">
            {all.groups.map((each, i) => (
              <button
                key={each.name}
                type="button"
                role="tab"
                aria-selected={i === group}
                aria-label={each.name}
                className={i === group ? 'emoji-tab is-on' : 'emoji-tab'}
                onClick={() => setGroup(i)}
              >
                {each.tab}
              </button>
            ))}
          </div>
        )}

        <div
          className="emoji-grid"
          role={found ? 'group' : 'tabpanel'}
          aria-label={found ? `${shown.length} found` : all.groups[group].name}
        >
          {shown.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="emoji-cell"
              aria-label={EMOJI_WORDS[emoji] ?? all.words[emoji] ?? emoji}
              onClick={() => onPick(emoji)}
            >
              {emoji}
            </button>
          ))}
          {found && shown.length === 0 && (
            <span className="emoji-none">
              <span className="empty-state-mark" aria-hidden="true">
                <IconSearch size={22} />
              </span>
              <span className="empty-state-title">Nothing for &ldquo;{query.trim()}&rdquo;</span>
              <span className="empty-state-body">Try one word, like heart or party.</span>
            </span>
          )}
        </div>
      </div>
    </>
  );
}

export function ReactionBar({
  mine = [],
  lead,
  onPick,
  onPickingChange,
}: {
  mine?: string[];
  /** A key of the caller's own, first in the row: the feed's comment button. */
  lead?: ReactNode;
  onPick: (emoji: string) => void;
  /**
   * Told when the full picker opens and closes. A story runs on a timer and
   * would otherwise move on while the panel was still up, taking the photo
   * being reacted to with it.
   */
  onPickingChange?: (open: boolean) => void;
}) {
  const [picking, setPicking] = useState(false);

  const show = (open: boolean) => {
    setPicking(open);
    onPickingChange?.(open);
  };

  return (
    <div className="reaction-bar" role="group" aria-label="React">
      {lead}
      {COMMON_REACTIONS.map((emoji) => {
        const picked = mine.includes(emoji);
        return (
          <button
            key={emoji}
            type="button"
            className={picked ? 'reaction-key is-mine' : 'reaction-key'}
            aria-label={picked ? `Remove your ${emoji}` : `React ${emoji}`}
            aria-pressed={picked}
            onClick={() => onPick(emoji)}
          >
            {emoji}
          </button>
        );
      })}
      <button
        type="button"
        className="reaction-key reaction-more"
        aria-label="Pick any emoji"
        aria-expanded={picking}
        onClick={() => show(true)}
      >
        <IconPlus size={17} />
      </button>
      {picking && (
        <EmojiPicker
          onPick={(emoji) => {
            show(false);
            onPick(emoji);
          }}
          onClose={() => show(false)}
        />
      )}
    </div>
  );
}

/**
 * Nothing here, said the same way on every screen. `onDark` is for the photo
 * viewer, which is black in both themes, where the theme's grey would not read.
 */
export function EmptyState({
  title,
  body,
  icon,
  onDark = false,
  children,
}: {
  title: string;
  body: string;
  /** A mark over the words, so the screen reads as meant rather than broken. */
  icon?: ReactNode;
  onDark?: boolean;
  /** What to do about it, under the words: a Back button, say. */
  children?: ReactNode;
}) {
  return (
    <div className={onDark ? 'empty-state on-dark' : 'empty-state'}>
      {icon && (
        <span className="empty-state-mark" aria-hidden="true">
          {icon}
        </span>
      )}
      <span className="empty-state-title">{title}</span>
      <span className="empty-state-body">{body}</span>
      {children}
    </div>
  );
}

/**
 * A line that says something happened, then takes itself away.
 *
 * Keeping a photo takes as long as R2 takes to copy the bytes, which is
 * seconds. Without this the button was pressed, nothing moved, and the only
 * way to find out whether it had worked was to close the story and open it
 * again — by which time it had.
 */
export function Toast({
  text,
  tone,
  onDone,
}: {
  text: string;
  tone?: 'bad';
  onDone: () => void;
}) {
  // The caller keys this on the text, so saying the same thing twice starts
  // the clock again rather than letting the first one carry the second away.
  useEffect(() => {
    const timer = window.setTimeout(onDone, 2600);
    return () => window.clearTimeout(timer);
  }, [text, onDone]);

  return (
    <span className={tone === 'bad' ? 'toast is-bad' : 'toast'} role="status">
      {text}
    </span>
  );
}

/**
 * Asks first, for the things that do not come back.
 *
 * Unkeeping is one of them: it puts the post back on the clock, and a post
 * whose days ran out while it was kept goes immediately, bytes and all.
 */
export function Confirm({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useBackCloses(onCancel);
  return (
    <>
      <button
        className="scrim confirm-scrim"
        aria-label="Cancel"
        onClick={onCancel}
        style={{ border: 0, padding: 0 }}
      />
      <div className="confirm" role="dialog" aria-modal="true" aria-label={title}>
        <span style={{ fontSize: 17, fontWeight: 700 }}>{title}</span>
        <span className="muted small" style={{ lineHeight: 1.5 }}>
          {body}
        </span>
        <div className="row" style={{ gap: 10, marginTop: 6 }}>
          <button className="btn btn--ghost grow" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn--danger grow" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}
